import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ClearIcon from "@mui/icons-material/Clear";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DriveFileMoveOutlinedIcon from "@mui/icons-material/DriveFileMoveOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import {
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import * as React from "react";

import { Menu } from "@/shared/components/Menu";
import { RobotoIconButton } from "@/shared/components/RobotoIconButton";
import { RobotoMenuItem } from "@/shared/components/RobotoMenuItem";
import { RobotoStyledPopoverMenu } from "@/shared/components/RobotoStyledPopoverMenu";
import { createEmptyLayout, LayoutRecord } from "@/shared/domain/layouts";
import { LayoutAccessibility } from "@/shared/domain/layouts/LayoutRecord";
import { Button } from "@/shared/foundation/primitives";

import { useActiveLayout } from "../../WorkspaceCtx/hooks";

import { LayoutFolderGroup, LayoutOption } from "./layoutFolders";
import { LayoutMenuItem } from "./LayoutMenuItem";
import { LayoutMenuSection } from "./LayoutMenuSection";
import styles from "./SelectLayoutMenu.module.css";
import { useLayoutOptions } from "./useLayoutOptions";

interface SelectLayoutMenuProps {
  anchorEl: HTMLElement | null;
  isOpen: boolean;
  onClose: () => void;
  onOpenContextMenu: (
    event: React.MouseEvent<HTMLElement>,
    layout: LayoutRecord,
  ) => void;
  onOpenFolderMenu: (
    event: React.MouseEvent<HTMLElement>,
    group: LayoutFolderGroup,
  ) => void;
  onSaveLayout: () => void;
  onSelectLayout: (layout: LayoutRecord) => void;
  onMoveSelected: (layouts: LayoutRecord[]) => void;
  onDeleteSelected: (layouts: LayoutRecord[]) => void;
  onRenameLayout: (layout: LayoutRecord) => void;
  onShareLayout: (layout: LayoutRecord) => void;
}

export function SelectLayoutMenu({
  anchorEl,
  isOpen,
  onClose,
  ...menuItemProps
}: SelectLayoutMenuProps) {
  return (
    <Menu
      id="select-layout-menu"
      anchorEl={anchorEl}
      open={isOpen}
      onClose={onClose}
      slotProps={{
        paper: {
          style: {
            // 900px caps the height on large screens; 75vh keeps it off the edges on small ones.
            maxHeight: "min(900px, 75vh)",
            // Wide enough that the search placeholder isn't truncated.
            minWidth: 320,
          },
        },
      }}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
    >
      <LayoutMenuItems {...menuItemProps} />
    </Menu>
  );
}

type LayoutMenuItemsProps = Omit<
  SelectLayoutMenuProps,
  "anchorEl" | "isOpen" | "onClose"
>;

function LayoutMenuItems({
  onSaveLayout,
  onSelectLayout,
  onOpenContextMenu,
  onOpenFolderMenu,
  onMoveSelected,
  onDeleteSelected,
  onRenameLayout,
  onShareLayout,
}: LayoutMenuItemsProps) {
  const [activeLayout] = useActiveLayout();
  const [search, setSearch] = React.useState("");
  const [selectMode, setSelectMode] = React.useState(false);
  const [selected, setSelected] = React.useState<Map<string, LayoutRecord>>(
    new Map(),
  );
  // Personal/Team start collapsed; Recently modified starts open.
  const [personalExpanded, setPersonalExpanded] = React.useState(false);
  const [teamExpanded, setTeamExpanded] = React.useState(false);
  const [recentExpanded, setRecentExpanded] = React.useState(true);
  const layoutOptions = useLayoutOptions(search);

  if (layoutOptions.isPending) {
    return (
      <MenuItem sx={{ typography: "body2", gap: 1.5 }} disableRipple>
        <CircularProgress size={16} />
        <Typography variant="body2">Loading layouts...</Typography>
      </MenuItem>
    );
  }

  if (layoutOptions.isError) {
    return (
      <MenuItem
        disableRipple
        sx={{
          typography: "body2",
          color: "error.main",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 1,
        }}
      >
        <p>Failed to load layouts</p>
        <Button
          size="small"
          variant="outlined"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            void layoutOptions.refetch();
          }}
          startIcon={<RefreshIcon />}
        >
          Try Again
        </Button>
      </MenuItem>
    );
  }

  const isSearching = search.trim().length > 0;

  const visibleOptions = [
    ...layoutOptions.personalGroups,
    ...layoutOptions.teamGroups,
  ].flatMap((group) => group.layouts);
  const allVisibleSelected =
    visibleOptions.length > 0 &&
    visibleOptions.every((option) => selected.has(option.id));

  const toggleSelect = (layout: LayoutRecord) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(layout.layout_id)) {
        next.delete(layout.layout_id);
      } else {
        next.set(layout.layout_id, layout);
      }
      return next;
    });
  };

  const setOptionsSelected = (options: LayoutOption[], select: boolean) => {
    setSelected((prev) => {
      const next = new Map(prev);
      options.forEach((option) => {
        if (select) {
          next.set(option.id, option.record);
        } else {
          next.delete(option.id);
        }
      });
      return next;
    });
  };

  const toggleSelectAll = () =>
    setOptionsSelected(visibleOptions, !allVisibleSelected);

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelected(new Map());
  };

  // Run a selection action, then leave select mode (the selection is spent afterward).
  // Whether the outer menu also closes is up to the handler: move/delete close it,
  // rename/share leave it open behind their dialog.
  const runSelectionAction = (action: () => void) => {
    action();
    exitSelectMode();
  };

  const selectedLayouts = [...selected.values()];

  const itemHandlers: LayoutItemHandlers = {
    selectMode,
    selectedIds: selected,
    onToggleSelect: toggleSelect,
    onSetOptionsSelected: setOptionsSelected,
    activeLayoutId: activeLayout.layout_id,
    onSelect: onSelectLayout,
    onOpenContextMenu,
    onOpenFolderMenu,
  };

  // Select mode expands the sections so items are checkable; search expands them so matches show.
  const sectionsForced = isSearching || selectMode;

  return (
    <>
      <RobotoMenuItem
        eventName={"OpenSaveLayoutConfirmationClicked"}
        onClick={onSaveLayout}
        disableRipple
        sx={{ py: 1.2 }}
      >
        <SaveIcon fontSize="small" sx={{ color: "grey.700" }} />
        <Typography variant="body2">Save layout</Typography>
      </RobotoMenuItem>
      <RobotoMenuItem
        disableRipple
        eventName={"ClearLayoutClicked"}
        onClick={() => onSelectLayout(createEmptyLayout())}
        sx={{ py: 1.2 }}
      >
        <ClearIcon fontSize="small" sx={{ color: "grey.700" }} />
        <Typography variant="body2">Clear layout</Typography>
      </RobotoMenuItem>
      <Divider />

      {/* Keep keydown off the Menu so typing here doesn't trigger its typeahead item navigation. */}
      <Box sx={{ px: 2, py: 1 }} onKeyDown={(event) => event.stopPropagation()}>
        <TextField
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search layouts and folders"
          size="small"
          fullWidth
          // Match the height of the layout dropdown field that opens this menu (2rem), and the
          // 14px text size of the menu rows around it.
          sx={{
            "& .MuiOutlinedInput-root": { height: "2rem" },
            "& .MuiOutlinedInput-input": { py: 0, fontSize: "0.875rem" },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: isSearching ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => setSearch("")}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        />
      </Box>

      <Box
        // Split the vertical padding so the button drops off the search box a little, tightening
        // the gap down to the first section without moving that section.
        sx={{
          px: 2,
          pt: 0.5,
          pb: 0.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {selectMode ? (
          <>
            <LayoutSelectionActions
              selectedLayouts={selectedLayouts}
              allVisibleSelected={allVisibleSelected}
              onToggleSelectAll={toggleSelectAll}
              onMove={() =>
                runSelectionAction(() => onMoveSelected(selectedLayouts))
              }
              onDelete={() =>
                runSelectionAction(() => onDeleteSelected(selectedLayouts))
              }
              onRename={(layout) =>
                runSelectionAction(() => onRenameLayout(layout))
              }
              onShare={(layout) =>
                runSelectionAction(() => onShareLayout(layout))
              }
            />
            <Button
              variant="text"
              size="small"
              sx={{ height: "32px" }}
              onClick={exitSelectMode}
            >
              Cancel
            </Button>
          </>
        ) : (
          <Button
            variant="text"
            size="small"
            sx={{ height: "32px" }}
            onClick={() => setSelectMode(true)}
          >
            Select
          </Button>
        )}
      </Box>

      {!isSearching && layoutOptions.recentOptions.length > 0 && (
        <LayoutMenuSection
          title="Recently modified"
          hasNoLayouts={false}
          expanded={recentExpanded}
          onToggle={() => setRecentExpanded((expanded) => !expanded)}
        >
          {layoutOptions.recentOptions.map((option) => (
            <LayoutOptionItem
              key={`recent-${option.id}`}
              option={option}
              {...itemHandlers}
            />
          ))}
        </LayoutMenuSection>
      )}

      <LayoutMenuSection
        title="Personal Layouts"
        hasNoLayouts={layoutOptions.personalGroups.length === 0}
        hasNoLayoutsMessage={
          isSearching
            ? "No matching personal layouts"
            : "No personal layouts yet"
        }
        expanded={sectionsForced || personalExpanded}
        onToggle={() => setPersonalExpanded((expanded) => !expanded)}
      >
        <LayoutGroups groups={layoutOptions.personalGroups} {...itemHandlers} />
      </LayoutMenuSection>

      <LayoutMenuSection
        title="Team Layouts"
        hasNoLayouts={layoutOptions.teamGroups.length === 0}
        hasNoLayoutsMessage={
          isSearching ? "No matching team layouts" : "No team layouts yet"
        }
        expanded={sectionsForced || teamExpanded}
        onToggle={() => setTeamExpanded((expanded) => !expanded)}
      >
        <LayoutGroups groups={layoutOptions.teamGroups} {...itemHandlers} />
      </LayoutMenuSection>
    </>
  );
}

/** The `Selected (N)` dropdown of bulk actions, styled like the dataset selection menu. */
function LayoutSelectionActions({
  selectedLayouts,
  allVisibleSelected,
  onToggleSelectAll,
  onMove,
  onDelete,
  onRename,
  onShare,
}: {
  selectedLayouts: LayoutRecord[];
  allVisibleSelected: boolean;
  onToggleSelectAll: () => void;
  onMove: () => void;
  onDelete: () => void;
  onRename: (layout: LayoutRecord) => void;
  onShare: (layout: LayoutRecord) => void;
}) {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const count = selectedLayouts.length;
  // Rename and Share only make sense for a single layout.
  const single = count === 1 ? selectedLayouts[0] : null;

  const close = () => setAnchorEl(null);
  const run = (action: () => void) => {
    close();
    action();
  };

  return (
    <>
      <Button
        variant="contained"
        size="small"
        sx={{ height: "32px" }}
        endIcon={<KeyboardArrowDownIcon />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        Selected ({count})
      </Button>
      <RobotoStyledPopoverMenu
        anchorEl={anchorEl}
        open={anchorEl !== null}
        onClose={close}
      >
        <MenuItem onClick={() => run(onToggleSelectAll)}>
          <SelectAllIcon />
          {allVisibleSelected ? "Deselect all" : "Select all"}
        </MenuItem>
        <RobotoMenuItem
          eventName="MoveSelectedLayoutsOptionClicked"
          eventProperties={{ count }}
          disabled={count === 0}
          onClick={() => run(onMove)}
        >
          <DriveFileMoveOutlinedIcon />
          Move to folder
        </RobotoMenuItem>
        {single && (
          <MenuItem onClick={() => run(() => onRename(single))}>
            <EditOutlinedIcon />
            Rename
          </MenuItem>
        )}
        {single && single.accessibility === LayoutAccessibility.User && (
          <MenuItem onClick={() => run(() => onShare(single))}>
            <GroupAddOutlinedIcon />
            Share with team
          </MenuItem>
        )}
        <MenuItem
          disabled={count === 0}
          sx={{ color: "error.main" }}
          onClick={() => run(onDelete)}
        >
          <DeleteOutlineIcon />
          Delete
        </MenuItem>
      </RobotoStyledPopoverMenu>
    </>
  );
}

interface LayoutItemHandlers {
  activeLayoutId?: string;
  selectMode: boolean;
  selectedIds: Map<string, LayoutRecord>;
  onToggleSelect: (layout: LayoutRecord) => void;
  onSetOptionsSelected: (options: LayoutOption[], selected: boolean) => void;
  onSelect: (layout: LayoutRecord) => void;
  onOpenContextMenu: (
    event: React.MouseEvent<HTMLElement>,
    layout: LayoutRecord,
  ) => void;
  onOpenFolderMenu: (
    event: React.MouseEvent<HTMLElement>,
    group: LayoutFolderGroup,
  ) => void;
}

function LayoutOptionItem({
  option,
  isNested,
  activeLayoutId,
  selectMode,
  selectedIds,
  onToggleSelect,
  onSelect,
  onOpenContextMenu,
}: {
  option: LayoutFolderGroup["layouts"][number];
  isNested?: boolean;
} & LayoutItemHandlers) {
  return (
    <LayoutMenuItem
      isNested={isNested}
      isActive={activeLayoutId === option.id}
      isDefault={option.isDefault}
      layout={option.record}
      onSelect={onSelect}
      onOpenContextMenu={onOpenContextMenu}
      selectMode={selectMode}
      isSelected={selectedIds.has(option.id)}
      onToggleSelect={onToggleSelect}
    />
  );
}

/** The null-folder group renders flat; named folders render as collapsible sections. */
function LayoutGroups({
  groups,
  ...handlers
}: { groups: LayoutFolderGroup[] } & LayoutItemHandlers) {
  return (
    <>
      {groups.map((group) => {
        if (group.folder === null) {
          return (
            <React.Fragment key="__ungrouped__">
              {group.layouts.map((option) => (
                <LayoutOptionItem
                  key={option.id}
                  option={option}
                  {...handlers}
                />
              ))}
            </React.Fragment>
          );
        }

        return (
          <LayoutFolderGroupView
            key={group.folder}
            folder={group.folder}
            group={group}
            {...handlers}
          />
        );
      })}
    </>
  );
}

function LayoutFolderGroupView({
  folder,
  group,
  ...handlers
}: { folder: string; group: LayoutFolderGroup } & LayoutItemHandlers) {
  const { selectMode, selectedIds, onSetOptionsSelected, onOpenFolderMenu } =
    handlers;
  const [isExpanded, setIsExpanded] = React.useState(true);

  const allChildrenSelected =
    group.layouts.length > 0 &&
    group.layouts.every((option) => selectedIds.has(option.id));
  const someChildrenSelected = group.layouts.some((option) =>
    selectedIds.has(option.id),
  );

  const chevron = isExpanded ? (
    <ExpandMoreIcon className={styles.folderChevron} />
  ) : (
    <ChevronRightIcon className={styles.folderChevron} />
  );

  return (
    <>
      <MenuItem
        disableRipple
        onClick={(event) => {
          event.stopPropagation();
          if (selectMode) {
            onSetOptionsSelected(group.layouts, !allChildrenSelected);
          } else {
            setIsExpanded((expanded) => !expanded);
          }
        }}
        className={styles.folderHeader}
      >
        {/* Select mode swaps the chevron for a select-all checkbox in the same 20px slot. */}
        {selectMode ? (
          <Checkbox
            checked={allChildrenSelected}
            indeterminate={someChildrenSelected && !allChildrenSelected}
            readOnly
            size="small"
            tabIndex={-1}
            disableRipple
            className={styles.folderCheckbox}
            sx={{ p: 0 }}
            inputProps={{ "aria-label": `Select all layouts in ${folder}` }}
          />
        ) : (
          chevron
        )}
        <Typography
          variant="body2"
          fontWeight={600}
          className={styles.folderName}
          title={folder}
        >
          {folder}
        </Typography>
        {!selectMode && (
          <RobotoIconButton
            eventName="LayoutFolderContextMenuOpened"
            eventProperties={{ folder }}
            size="small"
            aria-label="Folder actions"
            onClick={(event) => onOpenFolderMenu(event, group)}
          >
            <MoreVertIcon className={styles.folderMenuIcon} />
          </RobotoIconButton>
        )}
      </MenuItem>
      {/* With the chevron gone in select mode, children must stay visible to be checkable. */}
      {(selectMode || isExpanded) &&
        group.layouts.map((option) => (
          <LayoutOptionItem
            key={option.id}
            option={option}
            isNested
            {...handlers}
          />
        ))}
    </>
  );
}
